import axios from 'axios';
import { BusinessPlan, BusinessUser, RegisterBusinessUserRequest, PaymentIntent, CreatePaymentIntentRequest } from '../types/business';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add request interceptor to include auth token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Business endpoints
export const getBusinessPlans = () => 
    api.get<BusinessPlan[]>('/api/business/plans').then(res => res.data);

export const registerBusinessUser = async (userData: RegisterBusinessUserRequest) => {
  try {
    const response = await api.post('api/business/register', userData);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Registration failed');
    }
    throw new Error('Network error. Please try again.');
  }
};

export const createPaymentIntent = async (data: CreatePaymentIntentRequest) => {
  try {
    console.log('Creating payment intent', data);
    const response = await api.post('api/business/payment-intent', data);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to create payment intent');
    }
    throw new Error('Network error. Please try again.');
  }
};

export interface User {
  id: string;
  age?: number;
  gender?: string;
  isRegistered: boolean;
  username?: string;
  email?: string;
  shortBio?: string;
  whoAmI?: string;
  interests?: string;
  connectionId?: string;
  createdAt?: string;
  lastActive?: string;
  slots?: Slot[];
}

export interface Slot {
  id: number;
  position: number;
  isOuter: boolean;
  userId?: string;
  occupiedAt?: string;
  wheelId: string;
  user?: User;
}

export interface Wheel {
  id: string;
  name: string;
  description: string;
  slotCount: number;
  currentRound: number;
  lastSpinDate?: string;
  slots: Slot[];
  activeUsers: User[];
  registrationFields?: string; // JSON string with registration field configuration
}

export interface RegisterUserRequest {
  sessionId: string;
  age?: number;
  gender?: string;
  shortBio?: string;
  whoAmI?: string;
  interests?: string;
  connectionId?: string;
}

export const wheelApi = {
  getPublicWheel: async () => {
    const response = await api.get('api/wheels/public');
    return response.data;
  },
  
  getWheelById: async (wheelId: string) => {
    const response = await api.get(`api/wheels/${wheelId}`);
    return response.data;
  },
  
  getWheelByAccessCode: async (wheelId: string, accessCode: string) => {
    const response = await api.post(`api/wheels/${wheelId}/access`, { accessCode });
    return response.data;
  },

  getLatestSpinDate: async (): Promise<string | null> => {
    const response = await api.get<string>('/api/wheels/public/latest-spin');
    return response.data;
  },

  registerUser: async (data: RegisterUserRequest): Promise<User> => {
    try {
      const response = await api.post<User>('/api/users/anonymous', data);
      return response.data;
    } catch (error: any) {
      if (error.response.data) {
        alert(error.response.data || 'Failed to register user');
      }
      alert('Network error. Please try again.');
      throw error; // Re-throw the error to ensure a consistent return type
    }
  },

  takeSlot: async (wheelId: string, userId: string, slotId: number): Promise<void> => {
    await api.post(`/api/wheels/${wheelId}/slots/${slotId}/take`, { userId });
  },

  leaveSlot: async (wheelId: string, userId: string): Promise<void> => {
    await api.post(`/api/wheels/${wheelId}/slots/leave`, { userId });
  },
};

export interface DemoRequest {
  name: string;
  telephone: string;
  company: string;
  email: string;
}

export const requestDemo = async (data: DemoRequest) => {
  try {
    const response = await api.post('/api/business/demo-request', data);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to send demo request');
    }
    throw new Error('Network error. Please try again.');
  }
};

// Get business user profile
export const getBusinessUserProfile = async () => {
  try {
    const response = await api.get('/business/profile');
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch profile');
    }
    throw new Error('Network error. Please try again.');
  }
};

// Update business user profile
export const updateBusinessUserProfile = async (profileData: Partial<RegisterBusinessUserRequest>) => {
  try {
    const response = await api.put('/business/profile', profileData);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to update profile');
    }
    throw new Error('Network error. Please try again.');
  }
};

// Cancel subscription
export const cancelSubscription = async () => {
  try {
    const response = await api.post('/business/cancel-subscription');
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to cancel subscription');
    }
    throw new Error('Network error. Please try again.');
  }
};

export default api; 